import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Http from "../../Http";
import { Blog_Api } from "../../Helper";

const SingleBlogNew = () => {
  const { id } = useParams(); // `id` here is the `content_url`
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchProgress, setSearchProgress] = useState(0);

  // Cache for storing fetched pages (persists during session)
  const pageCache = useRef(new Map());

  useEffect(() => {
    // Helper: Fetch multiple pages in parallel with caching
    const fetchPagesInParallel = async (pageNumbers) => {
      const promises = pageNumbers.map(async (page) => {
        // Check cache first
        if (pageCache.current.has(page)) {
          return pageCache.current.get(page);
        }

        try {
          const data = await Http.get(`${Blog_Api}?page=${page}`);
          // Cache the result
          pageCache.current.set(page, data);
          return data;
        } catch (err) {
          console.error(`Error fetching page ${page}:`, err);
          return null;
        }
      });

      return Promise.all(promises);
    };

    // Helper: Find blog in page data
    const findBlogInPage = (pageData) => {
      if (pageData && pageData.blogs && pageData.blogs.length > 0) {
        return pageData.blogs.find(b => b.content_url === id);
      }
      return null;
    };

    const fetchBlog = async () => {
      try {
        setLoading(true);
        setSearchProgress(0);

        // STRATEGY 1: Check first 3 pages in parallel (most blogs are recent)
        const recentPages = [1, 2, 3];
        const recentResults = await fetchPagesInParallel(recentPages);
        
        setSearchProgress(20);

        // Check if blog found in recent pages
        for (const pageData of recentResults) {
          const foundBlog = findBlogInPage(pageData);
          if (foundBlog) {
            setBlog(foundBlog);
            setSearchProgress(100);
            return;
          }
        }

        // STRATEGY 2: Get total pages from first page
        const firstPageData = recentResults[0];
        if (!firstPageData || !firstPageData.total_count) {
          setError("Failed to load blog data");
          return;
        }

        const totalPages = Math.ceil(
          firstPageData.total_count / (firstPageData.per_page || 9)
        );

        setSearchProgress(30);

        // STRATEGY 3: Search remaining pages in parallel chunks
        const CHUNK_SIZE = 10; // Fetch 10 pages at once
        
        for (let chunkStart = 4; chunkStart <= totalPages; chunkStart += CHUNK_SIZE) {
          const chunkEnd = Math.min(chunkStart + CHUNK_SIZE - 1, totalPages);
          const pagesToFetch = [];

          for (let page = chunkStart; page <= chunkEnd; page++) {
            pagesToFetch.push(page);
          }

          // Fetch chunk in parallel
          const chunkResults = await fetchPagesInParallel(pagesToFetch);
          
          // Update progress
          const progress = 30 + ((chunkStart / totalPages) * 70);
          setSearchProgress(Math.min(progress, 95));

          // Check if blog found in this chunk
          for (const pageData of chunkResults) {
            const foundBlog = findBlogInPage(pageData);
            if (foundBlog) {
              setBlog(foundBlog);
              setSearchProgress(100);
              return;
            }
          }
        }

        // If we get here, blog wasn't found
        setError("Blog post not found");
        setSearchProgress(100);
        console.error("Blog not found in any page. ID:", id);
      } catch (error) {
        console.error("Error fetching blog:", error);
        setError("Failed to load blog post");
        setSearchProgress(100);
      } finally {
        setLoading(false);
      }
    };

    const fetchRecentBlogs = async () => {
      try {
        // Reuse cached first page if available
        let data;
        if (pageCache.current.has(1)) {
          data = pageCache.current.get(1);
        } else {
          data = await Http.get(`${Blog_Api}?page=1`);
          pageCache.current.set(1, data);
        }

        if (data && data.blogs) {
          const sortedRecentBlogs = data.blogs
            .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
            .slice(0, 8);

          setRecentBlogs(sortedRecentBlogs);
        }
      } catch (error) {
        console.error("Error fetching recent blogs:", error);
      }
    };

    if (id) {
      fetchBlog();
      fetchRecentBlogs();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setError("Invalid blog URL");
      setLoading(false);
    }
  }, [id]);

  // Format date for better display
  const formatDate = (dateString) => {
    if (!dateString) return "No date";
    
    try {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString;
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="container mx-auto p-4 py-8">
        <div className="mt-12 max-w-5xl mx-auto">
          {searchProgress > 0 && searchProgress < 100 && (
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${searchProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">
                Searching for blog... {Math.round(searchProgress)}%
              </p>
            </div>
          )}
          <div className="flex items-center mb-6">
            <Skeleton width={150} height={24} />
          </div>
          <Skeleton height={400} className="mb-6 rounded-lg" />
          <Skeleton height={60} width={400} className="mb-4" />
          <div className="flex mb-6">
            <Skeleton width={120} height={24} className="mr-4" />
            <Skeleton width={80} height={24} />
          </div>
          <Skeleton height={24} count={3} className="mb-2" />
          <Skeleton height={100} className="mb-4" />
          <Skeleton height={24} count={8} className="mb-2" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !blog) {
    return (
      <div className="container mx-auto p-4 py-8 mt-14">
        <div className="flex justify-center items-center h-64">
          <div className="text-center bg-gray-50 p-8 rounded-lg shadow-md">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto text-red-500 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-2xl font-bold text-red-500 mb-2">
              {error || "Blog post not found"}
            </h2>
            <p className="text-gray-600 mb-4">
              We couldn't find the blog post you're looking for.
            </p>
            <Link
              to="/blog"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300 inline-block"
            >
              Return to blog listing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.content_meta || blog.content_title}</title>
        <meta
          name="description"
          content={blog.content_meta_description || blog.content_title}
        />
        <meta name="keywords" content={blog.content_keyword} />
        <link
          rel="canonical"
          href={`https://thefinmaster.com/blog/${blog.content_url}`}
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>

      <div className="container mx-auto p-4 py-8">
        <div className="flex flex-wrap">
          <div className="w-full lg:w-3/4 lg:pr-8 py-8">
            <motion.div
              className="mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/blog"
                className="text-blue-600 hover:text-blue-800 transition-colors duration-300 flex items-center group"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2 group-hover:transform group-hover:-translate-x-1 transition-transform duration-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Blog List
              </Link>
            </motion.div>

            <motion.div
              className="overflow-hidden rounded-lg mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={blog.content_image}
                  alt={blog.content_title}
                  className="object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-700"
                />
              </div>
            </motion.div>

            <motion.h1
              className="text-3xl md:text-4xl font-bold mb-4 text-gray-800"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {blog.content_title}
            </motion.h1>

            <motion.div
              className="flex flex-wrap items-center text-gray-600 text-sm mb-8"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="mr-4 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="font-semibold">Published:</span>{" "}
                {formatDate(blog.content_date)}
              </span>

              {blog.content_category && (
                <span className="text-sm text-gray-600">
                  Category: {blog.content_category.trim()}
                </span>
              )}
            </motion.div>

            <motion.div
              className="prose max-w-none text-gray-700 mb-8"
              dangerouslySetInnerHTML={{ __html: blog.content_description }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-1/4 mt-8 lg:mt-8">
            <div className="bg-gray-50 p-4 rounded-lg shadow-md sticky top-24">
              <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">
                Recent Posts
              </h3>
              <div className="space-y-4">
                {recentBlogs.map(
                  (recentBlog, index) =>
                    recentBlog.content_url !== id && (
                      <motion.div
                        key={recentBlog.id || recentBlog.content_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Link
                          to={`/blog/${recentBlog.content_url}`}
                          className="flex hover:bg-gray-200 p-2 rounded-lg transition-all duration-300"
                        >
                          <img
                            src={recentBlog.content_image}
                            alt={recentBlog.content_title}
                            className="w-16 h-16 object-cover mr-2 rounded-lg"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors line-clamp-3 text-sm">
                              {recentBlog.content_title}
                            </h4>
                            <p className="text-gray-500 text-xs mt-1">
                              {formatDate(recentBlog.content_date)}
                            </p>
                          </div>
                        </Link>
                      </motion.div>
                    )
                )}
              </div>

              <div className="mt-8 pt-4 border-t">
                <Link
                  to="/blog"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-300"
                >
                  <span className="mr-2">View all blog posts</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>

              {/* Social Share Section */}
              {/* <div className="mt-8 pt-4 border-t">
                <h4 className="font-semibold mb-4">Share This Post</h4>
                <div className="flex space-x-2">
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                    </svg>
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.content_title)}&url=${window.location.href}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-blue-400 text-white p-2 rounded-full hover:bg-blue-500 transition-colors duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                    </svg>
                  </a>
                  <a 
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${encodeURIComponent(blog.content_title)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-blue-800 text-white p-2 rounded-full hover:bg-blue-900 transition-colors duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
                    </svg>
                  </a>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    }}
                    className="bg-gray-600 text-white p-2 rounded-full hover:bg-gray-700 transition-colors duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* CSS for blog content */}
      <style jsx>{`
        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
        }

        .blog-content h1,
        .blog-content h2,
        .blog-content h3,
        .blog-content h4 {
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          font-weight: bold;
        }

        .blog-content h1 {
          font-size: 1.875rem;
        }

        .blog-content h2 {
          font-size: 1.5rem;
        }

        .blog-content h3 {
          font-size: 1.25rem;
        }

        .blog-content p {
          margin-bottom: 1rem;
          line-height: 1.7;
        }

        .blog-content a {
          color: #2563eb;
          text-decoration: underline;
        }

        .blog-content ul,
        .blog-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }

        .blog-content ul li,
        .blog-content ol li {
          margin-bottom: 0.5rem;
        }

        .blog-content blockquote {
          border-left: 4px solid #3b82f6;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          font-style: italic;
          color: #4b5563;
        }
      `}</style>
    </>
  );
};

export default SingleBlogNew;

//NEw CODE WORKING 
// import React, { useState, useEffect } from "react";
// import { useParams, Link } from "react-router-dom";
// import Skeleton from "react-loading-skeleton";
// import "react-loading-skeleton/dist/skeleton.css";
// import { motion } from "framer-motion";
// import { Helmet } from "react-helmet";
// import Http from "../../Http";
// import { Blog_Api } from "../../Helper";

// const SingleBlogNew = () => {
//   const { id } = useParams(); // `id` here is the `content_url`
//   const [blog, setBlog] = useState(null);
//   const [relatedBlogs, setRelatedBlogs] = useState([]);
//   const [recentBlogs, setRecentBlogs] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // useEffect(() => {
//   //   const fetchBlog = async () => {
//   //     try {
//   //       setLoading(true);

//   //       // First, try to fetch the specific blog directly
//   //       const specificData = await Http.get(
//   //         `${Blog_Api}?url=${encodeURIComponent(id)}`
//   //       );

//   //       // Check if we found the blog in this response
//   //       if (
//   //         specificData &&
//   //         specificData.blogs &&
//   //         specificData.blogs.length > 0
//   //       ) {
//   //         const foundBlog = specificData.blogs.find(
//   //           (b) => b.content_url === id
//   //         );
//   //         if (foundBlog) {
//   //           setBlog(foundBlog);

//   //           // After finding the blog, fetch blogs from the same category
//   //           if (foundBlog.content_category) {
//   //             const categoryData = await Http.get(
//   //               `${Blog_Api}?category=${encodeURIComponent(
//   //                 foundBlog.content_category.trim()
//   //               )}`
//   //             );
//   //             if (categoryData && categoryData.blogs) {
//   //               // Filter out the current blog and take up to 4 related blogs
//   //               const related = categoryData.blogs
//   //                 .filter((b) => b.content_url !== id)
//   //                 .slice(0, 4);
//   //               setRelatedBlogs(related);
//   //             }
//   //           }

//   //           return;
//   //         }
//   //       }

//   //       // If not found with direct URL, search through all pages
//   //       let currentPage = 1;
//   //       const maxPages = 5; // Limit search to first 5 pages to avoid excessive API calls

//   //       while (currentPage <= maxPages) {
//   //         const pageData = await Http.get(`${Blog_Api}?page=${currentPage}`);

//   //         if (pageData && pageData.blogs && pageData.blogs.length > 0) {
//   //           const foundBlog = pageData.blogs.find((b) => b.content_url === id);
//   //           if (foundBlog) {
//   //             setBlog(foundBlog);

//   //             // After finding the blog, fetch blogs from the same category
//   //             if (foundBlog.content_category) {
//   //               const categoryData = await Http.get(
//   //                 `${Blog_Api}?category=${encodeURIComponent(
//   //                   foundBlog.content_category.trim()
//   //                 )}`
//   //               );
//   //               if (categoryData && categoryData.blogs) {
//   //                 // Filter out the current blog and take up to 4 related blogs
//   //                 const related = categoryData.blogs
//   //                   .filter((b) => b.content_url !== id)
//   //                   .slice(0, 4);
//   //                 setRelatedBlogs(related);
//   //               }
//   //             }

//   //             return;
//   //           }
//   //         }

//   //         currentPage++;
//   //       }

//   //       // If we get here, we couldn't find the blog
//   //       setError("Blog post not found");
//   //       console.error("Blog not found in any page. ID:", id);
//   //     } catch (error) {
//   //       console.error("Error fetching blog:", error);
//   //       setError("Failed to load blog post");
//   //     } finally {
//   //       setLoading(false);
//   //     }
//   //   };
// useEffect(() => {
//     const fetchBlog = async () => {
//       try {
//         setLoading(true);
//         let foundBlog = null;
//         let currentPage = 1;
//         let totalPages = 1;

//         // First, get the first page to determine total pages and search
//         const firstPageData = await Http.get(`${Blog_Api}?page=1`);
        
//         if (firstPageData && firstPageData.total_count) {
//           totalPages = Math.ceil(firstPageData.total_count / (firstPageData.per_page || 9));
          
//           // Check if the blog is in the first page
//           if (firstPageData.blogs && firstPageData.blogs.length > 0) {
//             foundBlog = firstPageData.blogs.find(b => b.content_url === id);
//             if (foundBlog) {
//               setBlog(foundBlog);
              
//               // Fetch related blogs from the same category
//               // if (foundBlog.content_category) {
//               //   await fetchRelatedBlogs(foundBlog.content_category, id);
//               // }
//               return;
//             }
//           }
//         }

//         // If not found in first page, search through all remaining pages
//         for (currentPage = 2; currentPage <= totalPages; currentPage++) {
//           try {
//             const pageData = await Http.get(`${Blog_Api}?page=${currentPage}`);
            
//             if (pageData && pageData.blogs && pageData.blogs.length > 0) {
//               foundBlog = pageData.blogs.find(b => b.content_url === id);
//               if (foundBlog) {
//                 setBlog(foundBlog);
                
//                 // Fetch related blogs from the same category
//                 // if (foundBlog.content_category) {
//                 //   await fetchRelatedBlogs(foundBlog.content_category, id);
//                 // }
//                 return;
//               }
//             }
//           } catch (pageError) {
//             console.error(`Error fetching page ${currentPage}:`, pageError);
//             continue; // Continue searching other pages
//           }
//         }

//         // If we get here, we couldn't find the blog
//         setError("Blog post not found");
//         console.error("Blog not found in any page. ID:", id);
//       } catch (error) {
//         console.error("Error fetching blog:", error);
//         setError("Failed to load blog post");
//       } finally {
//         setLoading(false);
//       }
//     };
//   //   const fetchRecentBlogs = async () => {
//   //     try {
//   //       // Get first page of blogs for recent posts
//   //       const data = await Http.get(`${Blog_Api}?page=1&limit=10`);

//   //       if (data && data.blogs) {
//   //         // Sort recent blogs by date (newest first)
//   //         const sortedRecentBlogs = data.blogs
//   //           .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
//   //           .slice(0, 8); // Fetch only the first 8 blogs for recent posts

//   //         setRecentBlogs(sortedRecentBlogs);
//   //       }
//   //     } catch (error) {
//   //       console.error("Error fetching recent blogs:", error);
//   //     }
//   //   };

//   //   if (id) {
//   //     fetchBlog();
//   //     fetchRecentBlogs();
//   //     // Scroll to top when loading a new blog
//   //     window.scrollTo({ top: 0, behavior: "smooth" });
//   //   } else {
//   //     setError("Invalid blog URL");
//   //   }
//   // }, [id]);

//   // // Format date for better display
//   // const formatDate = (dateString) => {
//   //   const options = { year: "numeric", month: "short", day: "numeric" };
//   //   return new Date(dateString).toLocaleDateString(undefined, options);
//   // };

//   // // Loading skeleton
//   // if (loading) {
//   //   return (
//   //     <div className="container mx-auto p-4 py-8">
//   //       <div className="mt-12 max-w-5xl mx-auto">
//   //         <div className="flex items-center mb-6">
//   //           <Skeleton width={150} height={24} />
//   //         </div>
//   //         <Skeleton height={400} className="mb-6 rounded-lg" />
//   //         <Skeleton height={60} width={400} className="mb-4" />
//   //         <div className="flex mb-6">
//   //           <Skeleton width={120} height={24} className="mr-4" />
//   //           <Skeleton width={80} height={24} />
//   //         </div>
//   //         <Skeleton height={24} count={3} className="mb-2" />
//   //         <Skeleton height={100} className="mb-4" />
//   //         <Skeleton height={24} count={8} className="mb-2" />
//   //       </div>
//   //     </div>
//   //   );
//   // }

//   // // Error state
//   // if (error || !blog) {
//   //   return (
//   //     <div className="container mx-auto p-4 py-8 mt-14">
//   //       <div className="flex justify-center items-center h-64">
//   //         <div className="text-center bg-gray-50 p-8 rounded-lg shadow-md">
//   //           <svg
//   //             xmlns="http://www.w3.org/2000/svg"
//   //             className="h-16 w-16 mx-auto text-red-500 mb-4"
//   //             fill="none"
//   //             viewBox="0 0 24 24"
//   //             stroke="currentColor"
//   //           >
//   //             <path
//   //               strokeLinecap="round"
//   //               strokeLinejoin="round"
//   //               strokeWidth={2}
//   //               d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
//   //             />
//   //           </svg>
//   //           <h2 className="text-2xl font-bold text-red-500 mb-2">
//   //             {error || "Blog post not found"}
//   //           </h2>
//   //           <p className="text-gray-600 mb-4">
//   //             We couldn't find the blog post you're looking for.
//   //           </p>
//   //           <Link
//   //             to="/blog"
//   //             className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300 inline-block"
//   //           >
//   //             Return to blog listing
//   //           </Link>
//   //         </div>
//   //       </div>
//   //     </div>
//   //   );
//   // }
// const fetchRecentBlogs = async () => {
//       try {
//         // Get first page of blogs for recent posts
//         const data = await Http.get(`${Blog_Api}?page=1`);

//         if (data && data.blogs) {
//           // Sort recent blogs by date (newest first)
//           const sortedRecentBlogs = data.blogs
//             .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
//             .slice(0, 8); // Fetch only the first 8 blogs for recent posts

//           setRecentBlogs(sortedRecentBlogs);
//         }
//       } catch (error) {
//         console.error("Error fetching recent blogs:", error);
//       }
//     };

//     if (id) {
//       fetchBlog();
//       fetchRecentBlogs();
//       // Scroll to top when loading a new blog
//       window.scrollTo({ top: 0, behavior: "smooth" });
//     } else {
//       setError("Invalid blog URL");
//     }
//   }, [id]);

//   // Format date for better display
//   const formatDate = (dateString) => {
//     if (!dateString) return "No date";
    
//     try {
//       const options = { year: "numeric", month: "short", day: "numeric" };
//       return new Date(dateString).toLocaleDateString(undefined, options);
//     } catch (error) {
//       console.error("Date formatting error:", error);
//       return dateString;
//     }
//   };

//   // Loading skeleton
//   if (loading) {
//     return (
//       <div className="container mx-auto p-4 py-8">
//         <div className="mt-12 max-w-5xl mx-auto">
//           <div className="flex items-center mb-6">
//             <Skeleton width={150} height={24} />
//           </div>
//           <Skeleton height={400} className="mb-6 rounded-lg" />
//           <Skeleton height={60} width={400} className="mb-4" />
//           <div className="flex mb-6">
//             <Skeleton width={120} height={24} className="mr-4" />
//             <Skeleton width={80} height={24} />
//           </div>
//           <Skeleton height={24} count={3} className="mb-2" />
//           <Skeleton height={100} className="mb-4" />
//           <Skeleton height={24} count={8} className="mb-2" />
//         </div>
//       </div>
//     );
//   }

//   // Error state
//   if (error || !blog) {
//     return (
//       <div className="container mx-auto p-4 py-8 mt-14">
//         <div className="flex justify-center items-center h-64">
//           <div className="text-center bg-gray-50 p-8 rounded-lg shadow-md">
//             <svg
//               xmlns="http://www.w3.org/2000/svg"
//               className="h-16 w-16 mx-auto text-red-500 mb-4"
//               fill="none"
//               viewBox="0 0 24 24"
//               stroke="currentColor"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
//               />
//             </svg>
//             <h2 className="text-2xl font-bold text-red-500 mb-2">
//               {error || "Blog post not found"}
//             </h2>
//             <p className="text-gray-600 mb-4">
//               We couldn't find the blog post you're looking for.
//             </p>
//             <Link
//               to="/blog"
//               className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300 inline-block"
//             >
//               Return to blog listing
//             </Link>
//           </div>
//         </div>
//       </div>
//     );
//   }
//   return (
//     <>
//       <Helmet>
//         <title>{blog.content_meta || blog.content_title}</title>
//         <meta
//           name="description"
//           content={blog.content_meta_description || blog.content_title}
//         />
//         <meta name="keywords" content={blog.content_keyword} />
//         <link
//           rel="canonical"
//           href={`https://thefinmaster.com/blog/${blog.content_url}`}
//         />
//         <meta
//           name="robots"
//           content="max-snippet:-1, max-image-preview:standard"
//         />
//         <meta name="robots" content="max-video-preview:-1" />
//       </Helmet>

//       <div className="container mx-auto p-4 py-8">
//         <div className="flex flex-wrap">
//           <div className="w-full lg:w-3/4 lg:pr-8 py-8">
//             <motion.div
//               className="mb-6"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             >
//               <Link
//                 to="/blog"
//                 className="text-blue-600 hover:text-blue-800 transition-colors duration-300 flex items-center group"
//               >
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   className="h-5 w-5 mr-2 group-hover:transform group-hover:-translate-x-1 transition-transform duration-300"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                   stroke="currentColor"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M15 19l-7-7 7-7"
//                   />
//                 </svg>
//                 Back to Blog List
//               </Link>
//             </motion.div>

//             <motion.div
//               className="overflow-hidden rounded-lg mb-6"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             >
//               {/* <img
//                 src={blog.content_image}
//                 alt={blog.content_title}
//                 className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-700"
//               /> */}
//               <div className="aspect-w-16 aspect-h-9">
//                 <img
//                   src={blog.content_image}
//                   alt={blog.content_title}
//                   className="object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-700"
//                 />
//               </div>
//             </motion.div>

//             <motion.h1
//               className="text-3xl md:text-4xl font-bold mb-4 text-gray-800"
//               initial={{ y: 30, opacity: 0 }}
//               animate={{ y: 0, opacity: 1 }}
//               transition={{ duration: 0.5, delay: 0.1 }}
//             >
//               {blog.content_title}
//             </motion.h1>

//             <motion.div
//               className="flex flex-wrap items-center text-gray-600 text-sm mb-8"
//               initial={{ y: 20, opacity: 0 }}
//               animate={{ y: 0, opacity: 1 }}
//               transition={{ duration: 0.5, delay: 0.2 }}
//             >
//               <span className="mr-4 flex items-center">
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   className="h-4 w-4 mr-1"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                   stroke="currentColor"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
//                   />
//                 </svg>
//                 <span className="font-semibold">Published:</span>{" "}
//                 {formatDate(blog.content_date)}
//               </span>

//               {blog.content_category && (
//                 <span className="text-sm text-gray-600">
//                   {/* Simple text display of category */}
//                   Category: {blog.content_category.trim()}
//                 </span>
//               )}
//             </motion.div>

//             <motion.div
//               className="prose max-w-none text-gray-700 mb-8 "
//               dangerouslySetInnerHTML={{ __html: blog.content_description }}
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5, delay: 0.3 }}
//             />
//           </div>

//           {/* Sidebar */}
//           <div className="w-full lg:w-1/4 mt-8 lg:mt-8">
//             <div className="bg-gray-50 p-4 rounded-lg shadow-md sticky top-24">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">
//                 Recent Posts
//               </h3>
//               <div className="space-y-4">
//                 {recentBlogs.map(
//                   (recentBlog, index) =>
//                recentBlog.content_url !== id && (
//                         <motion.div
//                         key={recentBlog.id || recentBlog.content_id}
//                         initial={{ opacity: 0, x: -20 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.3, delay: index * 0.1 }}
//                       >
//                         <Link
//                           to={`/blog/${recentBlog.content_url}`}
//                           className="flex hover:bg-gray-200 p-2 rounded-lg transition-all duration-300"
//                         >
//                           <img
//                             src={recentBlog.content_image}
//                             alt={recentBlog.content_title}
//                             className="w-16 h-16 object-cover mr-2 rounded-lg"
//                           />
//                           <div className="flex-1 min-w-0">
//                             <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors line-clamp-3 text-sm">
//                               {recentBlog.content_title}
//                             </h4>
//                             <p className="text-gray-500 text-xs mt-1">
//                               {formatDate(recentBlog.content_date)}
//                             </p>
//                           </div>
//                         </Link>
//                       </motion.div>
//                     )
//                 )}
//               </div>

//               <div className="mt-8 pt-4 border-t">
//                 <Link
//                   to="/blog"
//                   className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-300"
//                 >
//                   <span className="mr-2">View all blog posts</span>
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     className="h-4 w-4"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                     stroke="currentColor"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M9 5l7 7-7 7"
//                     />
//                   </svg>
//                 </Link>
//               </div>

//               {/* Social Share Section */}
//               {/* <div className="mt-8 pt-4 border-t">
//                 <h4 className="font-semibold mb-4">Share This Post</h4>
//                 <div className="flex space-x-2">
//                   <a 
//                     href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} 
//                     target="_blank" 
//                     rel="noopener noreferrer"
//                     className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
//                       <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
//                     </svg>
//                   </a>
//                   <a 
//                     href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.content_title)}&url=${window.location.href}`} 
//                     target="_blank" 
//                     rel="noopener noreferrer"
//                     className="bg-blue-400 text-white p-2 rounded-full hover:bg-blue-500 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
//                       <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
//                     </svg>
//                   </a>
//                   <a 
//                     href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${encodeURIComponent(blog.content_title)}`} 
//                     target="_blank" 
//                     rel="noopener noreferrer"
//                     className="bg-blue-800 text-white p-2 rounded-full hover:bg-blue-900 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
//                       <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
//                     </svg>
//                   </a>
//                   <button 
//                     onClick={() => {
//                       navigator.clipboard.writeText(window.location.href);
//                       alert('Link copied to clipboard!');
//                     }}
//                     className="bg-gray-600 text-white p-2 rounded-full hover:bg-gray-700 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
//                     </svg>
//                   </button>
//                 </div>
//               </div> */}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* CSS for blog content */}
//       <style jsx>{`
//         .blog-content img {
//           max-width: 100%;
//           height: auto;
//           border-radius: 0.5rem;
//           margin: 1.5rem 0;
//         }

//         .blog-content h1,
//         .blog-content h2,
//         .blog-content h3,
//         .blog-content h4 {
//           margin-top: 1.5rem;
//           margin-bottom: 1rem;
//           font-weight: bold;
//         }

//         .blog-content h1 {
//           font-size: 1.875rem;
//         }

//         .blog-content h2 {
//           font-size: 1.5rem;
//         }

//         .blog-content h3 {
//           font-size: 1.25rem;
//         }

//         .blog-content p {
//           margin-bottom: 1rem;
//           line-height: 1.7;
//         }

//         .blog-content a {
//           color: #2563eb;
//           text-decoration: underline;
//         }

//         .blog-content ul,
//         .blog-content ol {
//           margin-left: 1.5rem;
//           margin-bottom: 1rem;
//         }

//         .blog-content ul li,
//         .blog-content ol li {
//           margin-bottom: 0.5rem;
//         }

//         .blog-content blockquote {
//           border-left: 4px solid #3b82f6;
//           padding-left: 1rem;
//           margin-left: 0;
//           margin-right: 0;
//           font-style: italic;
//           color: #4b5563;
//         }
//       `}</style>
//     </>
//   );
// };

// export default SingleBlogNew;



//OLD CODE
// import React, { useState, useEffect } from "react";
// import { useParams, Link } from "react-router-dom";
// import Skeleton from "react-loading-skeleton";
// import 'react-loading-skeleton/dist/skeleton.css';
// import { motion } from "framer-motion";
// import { Helmet } from "react-helmet";
// import Http from "../../Http";
// import { Blog_Api } from "../../Helper";

// const SingleBlogNew = () => {
//   const { id } = useParams(); // `id` here is the `content_url`
//   const [blog, setBlog] = useState(null);
//   const [relatedBlogs, setRelatedBlogs] = useState([]);
//   const [recentBlogs, setRecentBlogs] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchBlog = async () => {
//       try {
//         setLoading(true);

//         // First, try to fetch the specific blog directly
//         const specificData = await Http.get(`${Blog_Api}?url=${encodeURIComponent(id)}`);

//         // Check if we found the blog in this response
//         if (specificData && specificData.blogs && specificData.blogs.length > 0) {
//           const foundBlog = specificData.blogs.find(b => b.content_url === id);
//           if (foundBlog) {
//             setBlog(foundBlog);

//             // After finding the blog, fetch blogs from the same category
//             if (foundBlog.content_category) {
//               const categoryData = await Http.get(`${Blog_Api}?category=${encodeURIComponent(foundBlog.content_category.trim())}`);
//               if (categoryData && categoryData.blogs) {
//                 // Filter out the current blog and take up to 4 related blogs
//                 const related = categoryData.blogs
//                   .filter(b => b.content_url !== id)
//                   .slice(0, 4);
//                 setRelatedBlogs(related);
//               }
//             }

//             return;
//           }
//         }

//         // If not found with direct URL, search through all pages
//         let currentPage = 1;
//         const maxPages = 5; // Limit search to first 5 pages to avoid excessive API calls

//         while (currentPage <= maxPages) {
//           const pageData = await Http.get(`${Blog_Api}?page=${currentPage}`);

//           if (pageData && pageData.blogs && pageData.blogs.length > 0) {
//             const foundBlog = pageData.blogs.find(b => b.content_url === id);
//             if (foundBlog) {
//               setBlog(foundBlog);

//               // After finding the blog, fetch blogs from the same category
//               if (foundBlog.content_category) {
//                 const categoryData = await Http.get(`${Blog_Api}?category=${encodeURIComponent(foundBlog.content_category.trim())}`);
//                 if (categoryData && categoryData.blogs) {
//                   // Filter out the current blog and take up to 4 related blogs
//                   const related = categoryData.blogs
//                     .filter(b => b.content_url !== id)
//                     .slice(0, 4);
//                   setRelatedBlogs(related);
//                 }
//               }

//               return;
//             }
//           }

//           currentPage++;
//         }

//         // If we get here, we couldn't find the blog
//         setError("Blog post not found");
//         console.error("Blog not found in any page. ID:", id);

//       } catch (error) {
//         console.error("Error fetching blog:", error);
//         setError("Failed to load blog post");
//       } finally {
//         setLoading(false);
//       }
//     };

//     const fetchRecentBlogs = async () => {
//       try {
//         // Get first page of blogs for recent posts
//         const data = await Http.get(`${Blog_Api}?page=1&limit=10`);

//         if (data && data.blogs) {
//           // Sort recent blogs by date (newest first)
//           const sortedRecentBlogs = data.blogs
//             .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
//             .slice(0, 8); // Fetch only the first 8 blogs for recent posts

//           setRecentBlogs(sortedRecentBlogs);
//         }
//       } catch (error) {
//         console.error("Error fetching recent blogs:", error);
//       }
//     };

//     if (id) {
//       fetchBlog();
//       fetchRecentBlogs();
//       // Scroll to top when loading a new blog
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//     } else {
//       setError("Invalid blog URL");
//     }
//   }, [id]);

//   // Format date for better display
//   const formatDate = (dateString) => {
//     const options = { year: 'numeric', month: 'short', day: 'numeric' };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   };

//   // Loading skeleton
//   if (loading) {
//     return (
//       <div className="container mx-auto p-4 py-8">
//         <div className="mt-12 max-w-5xl mx-auto">
//           <div className="flex items-center mb-6">
//             <Skeleton width={150} height={24} />
//           </div>
//           <Skeleton height={400} className="mb-6 rounded-lg" />
//           <Skeleton height={60} width={400} className="mb-4" />
//           <div className="flex mb-6">
//             <Skeleton width={120} height={24} className="mr-4" />
//             <Skeleton width={80} height={24} />
//           </div>
//           <Skeleton height={24} count={3} className="mb-2" />
//           <Skeleton height={100} className="mb-4" />
//           <Skeleton height={24} count={8} className="mb-2" />
//         </div>
//       </div>
//     );
//   }

//   // Error state
//   if (error || !blog) {
//     return (
//       <div className="container mx-auto p-4 py-8">
//         <div className="flex justify-center items-center h-64">
//           <div className="text-center bg-gray-50 p-8 rounded-lg shadow-md">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//             </svg>
//             <h2 className="text-2xl font-bold text-red-500 mb-2">{error || "Blog post not found"}</h2>
//             <p className="text-gray-600 mb-4">We couldn't find the blog post you're looking for.</p>
//             <Link to="/blog" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-300 inline-block">
//               Return to blog listing
//             </Link>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <Helmet>
//         <title>{blog.content_meta || blog.content_title}</title>
//         <meta name='description' content={blog.content_meta_description || blog.content_title} />
//         <meta name='keywords' content={blog.content_keyword || ''} />
//       </Helmet>

//       <div className="container mx-auto p-4 py-8">
//         <div className="flex flex-wrap">
//           <div className="w-full lg:w-3/4 lg:pr-8 py-8">
//             <motion.div
//               className="mb-6"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             >
//               <Link to="/blog" className="text-blue-600 hover:text-blue-800 transition-colors duration-300 flex items-center group">
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 group-hover:transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
//                 </svg>
//                 Back to Blog List
//               </Link>
//             </motion.div>

//             <motion.div
//               className="overflow-hidden rounded-lg mb-6"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5 }}
//             >
//               <img
//                 src={blog.content_image}
//                 alt={blog.content_title}
//                 className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-cover rounded-lg shadow-md hover:scale-105 transition-transform duration-700"
//               />
//             </motion.div>

//             <motion.h1
//               className="text-3xl md:text-4xl font-bold mb-4 text-gray-800"
//               initial={{ y: 30, opacity: 0 }}
//               animate={{ y: 0, opacity: 1 }}
//               transition={{ duration: 0.5, delay: 0.1 }}
//             >
//               {blog.content_title}
//             </motion.h1>

//             <motion.div
//               className="flex flex-wrap items-center text-gray-600 text-sm mb-8"
//               initial={{ y: 20, opacity: 0 }}
//               animate={{ y: 0, opacity: 1 }}
//               transition={{ duration: 0.5, delay: 0.2 }}
//             >
//               <span className="mr-4 flex items-center">
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
//                 </svg>
//                 <span className="font-semibold">Published:</span> {formatDate(blog.content_date)}
//               </span>

//               {blog.content_category && (
//                 <Link
//                   to={`/blog?category=${encodeURIComponent(blog.content_category.trim())}`}
//                   className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs hover:bg-blue-200 transition-colors duration-300 inline-flex items-center"
//                 >
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
//                   </svg>
//                   {blog.content_category.trim()}
//                 </Link>
//               )}
//             </motion.div>

//             <motion.div
//               className="prose max-w-none text-gray-700 mb-8 "
//               dangerouslySetInnerHTML={{ __html: blog.content_description }}
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ duration: 0.5, delay: 0.3 }}
//             />

//             {/* Related Posts Section */}
//             {/* {relatedBlogs.length > 0 && (
//               <motion.div
//                 className="mt-12 border-t pt-8"
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 1 }}
//                 transition={{ duration: 0.5, delay: 0.4 }}
//               >
//                 <h3 className="text-2xl font-bold mb-6 text-gray-800">Related Posts</h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   {relatedBlogs.map((relatedBlog) => (
//                     <Link
//                       key={relatedBlog.id || relatedBlog.content_id}
//                       to={`/blognew/${relatedBlog.content_url}`}
//                       className="flex border rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-300 bg-white"
//                     >
//                       <img
//                         src={relatedBlog.content_image}
//                         alt={relatedBlog.content_title}
//                         className="w-24 h-24 object-cover"
//                       />
//                       <div className="p-3">
//                         <h4 className="font-semibold line-clamp-2 text-sm hover:text-blue-600 transition-colors duration-300">
//                           {relatedBlog.content_title}
//                         </h4>
//                         <p className="text-gray-500 text-xs mt-1">
//                           {formatDate(relatedBlog.content_date)}
//                         </p>
//                       </div>
//                     </Link>
//                   ))}
//                 </div>
//               </motion.div>
//             )} */}
//           </div>

//           {/* Sidebar */}
//           <div className="w-full lg:w-1/4 mt-8 lg:mt-8">
//             <div className="bg-gray-50 p-4 rounded-lg shadow-md sticky top-24">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-500 pb-2">Recent Posts</h3>
//               <div className="space-y-4">
//                 {recentBlogs.map((recentBlog, index) => (
//                   recentBlog.content_url !== id && (
//                     <motion.div
//                       key={recentBlog.id || recentBlog.content_id}
//                       initial={{ opacity: 0, x: -20 }}
//                       animate={{ opacity: 1, x: 0 }}
//                       transition={{ duration: 0.3, delay: index * 0.1 }}
//                     >
//                       <Link
//                         to={`/blog/${recentBlog.content_url}`}
//                         className="flex hover:bg-gray-200 p-2 rounded-lg transition-all duration-300"
//                       >
//                         <img
//                           src={recentBlog.content_image}
//                           alt={recentBlog.content_title}
//                           className="w-16 h-16 object-cover mr-2 rounded-lg"
//                         />
//                         <div className="flex-1 min-w-0">
//                           <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors line-clamp-2 text-sm">
//                             {recentBlog.content_title}
//                           </h4>
//                           <p className="text-gray-500 text-xs mt-1">
//                             {formatDate(recentBlog.content_date)}
//                           </p>
//                         </div>
//                       </Link>
//                     </motion.div>
//                   )
//                 ))}
//               </div>

//               <div className="mt-8 pt-4 border-t">
//                 <Link
//                   to="/blog"
//                   className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-300"
//                 >
//                   <span className="mr-2">View all blog posts</span>
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//                   </svg>
//                 </Link>
//               </div>

//               {/* Social Share Section */}
//               {/* <div className="mt-8 pt-4 border-t">
//                 <h4 className="font-semibold mb-4">Share This Post</h4>
//                 <div className="flex space-x-2">
//                   <a
//                     href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
//                       <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
//                     </svg>
//                   </a>
//                   <a
//                     href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.content_title)}&url=${window.location.href}`}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="bg-blue-400 text-white p-2 rounded-full hover:bg-blue-500 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
//                       <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
//                     </svg>
//                   </a>
//                   <a
//                     href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${encodeURIComponent(blog.content_title)}`}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="bg-blue-800 text-white p-2 rounded-full hover:bg-blue-900 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
//                       <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
//                     </svg>
//                   </a>
//                   <button
//                     onClick={() => {
//                       navigator.clipboard.writeText(window.location.href);
//                       alert('Link copied to clipboard!');
//                     }}
//                     className="bg-gray-600 text-white p-2 rounded-full hover:bg-gray-700 transition-colors duration-300"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
//                     </svg>
//                   </button>
//                 </div>
//               </div> */}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* CSS for blog content */}
//       <style jsx>{`
//         .blog-content img {
//           max-width: 100%;
//           height: auto;
//           border-radius: 0.5rem;
//           margin: 1.5rem 0;
//         }

//         .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4 {
//           margin-top: 1.5rem;
//           margin-bottom: 1rem;
//           font-weight: bold;
//         }

//         .blog-content h1 {
//           font-size: 1.875rem;
//         }

//         .blog-content h2 {
//           font-size: 1.5rem;
//         }

//         .blog-content h3 {
//           font-size: 1.25rem;
//         }

//         .blog-content p {
//           margin-bottom: 1rem;
//           line-height: 1.7;
//         }

//         .blog-content a {
//           color: #2563eb;
//           text-decoration: underline;
//         }

//         .blog-content ul, .blog-content ol {
//           margin-left: 1.5rem;
//           margin-bottom: 1rem;
//         }

//         .blog-content ul li, .blog-content ol li {
//           margin-bottom: 0.5rem;
//         }

//         .blog-content blockquote {
//           border-left: 4px solid #3b82f6;
//           padding-left: 1rem;
//           margin-left: 0;
//           margin-right: 0;
//           font-style: italic;
//           color: #4b5563;
//         }
//       `}</style>
//     </>
//   );
// };

// export default SingleBlogNew;
